// src/screens/DiningReservations.jsx
import useIdleLogout from "../hooks/useIdleLogout";
import "../css/DiningReservations.css";
import "bootstrap-icons/font/bootstrap-icons.css";

import { useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from "chart.js";
ChartJS.register(ArcElement, Tooltip, Legend);

const PALETTE = [
  "#0d6efd", "#198754", "#dc3545", "#ffc107", "#20c997",
  "#6f42c1", "#fd7e14", "#0dcaf0", "#6610f2", "#1982c4"
];

const unique = (list) => Array.from(new Set(list.filter(Boolean)));

export default function DiningReservations() {
  useIdleLogout();

  const [reservations] = useState([]);

  /* ========================= METRICS ========================= */
  const totalReservations = reservations.length;

  const inProgress = useMemo(() => {
    const set = new Set(["pending", "confirmed", "seated", "in_progress", "processing"]);
    return reservations.filter((r) => set.has(String(r.status || "").toLowerCase())).length;
  }, [reservations]);

  const completed = useMemo(() => {
    const set = new Set(["completed", "done"]);
    return reservations.filter((r) => set.has(String(r.status || "").toLowerCase())).length;
  }, [reservations]);

  const declined = useMemo(() => {
    const set = new Set(["cancelled", "canceled", "declined", "rejected", "no_show"]);
    return reservations.filter((r) => set.has(String(r.status || "").toLowerCase())).length;
  }, [reservations]);

  /* ========================= CHARTS ========================= */
  // ✅ Chart 1: Restaurant Names
  const restaurantLabels = useMemo(() => {
    const labels = unique(reservations.map((r) => (r.restaurant || r.restaurant_name || "").toString().trim()));
    return labels.length ? labels : ["No Data"];
  }, [reservations]);

  const restaurantDonut = useMemo(() => {
    const labels = restaurantLabels;
    const data =
      labels[0] === "No Data"
        ? [1]
        : labels.map((lbl) =>
          reservations.filter(
            (r) => ((r.restaurant || r.restaurant_name || "").toString().trim() || "").toLowerCase() === lbl.toLowerCase()
          ).length
        );

    return {
      labels,
      datasets: [{ data, backgroundColor: labels.map((_, i) => PALETTE[i % PALETTE.length]) }],
    };
  }, [restaurantLabels, reservations]);

  // ✅ Chart 2: Status Breakdown (Completed Only) — (shows completed/no data)
  const completedRows = useMemo(() => {
    const done = new Set(["completed", "done"]);
    return reservations.filter((r) => done.has(String(r.status || "").toLowerCase()));
  }, [reservations]);

  const statusLabels = useMemo(() => {
    const labels = unique(completedRows.map((r) => (r.status || "Unknown").toString().trim() || "Unknown"));
    return labels.length ? labels : ["No Data"];
  }, [completedRows]);

  const statusDonut = useMemo(() => {
    const labels = statusLabels;
    const data =
      labels[0] === "No Data"
        ? [1]
        : labels.map((s) =>
          completedRows.filter((r) => (String(r.status || "Unknown").trim() || "Unknown") === s).length
        );

    return {
      labels,
      datasets: [{ data, backgroundColor: labels.map((_, i) => PALETTE[(i + 3) % PALETTE.length]) }],
    };
  }, [statusLabels, completedRows]);

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 12, padding: 10, font: { size: 11 } } },
      tooltip: {
        callbacks: { label: (ctx) => `${ctx.label || ""}: ${ctx.raw ?? 0}` },
      },
    },
  };

  const handleExportExcel = () => alert("Export Excel (hook later)");
  const handleExportPDF = () => alert("Export PDF (hook later)");

  return (
    <div className="container-fluid px-4 dining-reservations-page">
      <section className="mb-5">
        {/* ✅ Metrics */}
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 dining-metrics">
          {[
            { label: "Total Reservations", value: totalReservations },
            { label: "In Progress", value: inProgress },
            { label: "Completed", value: completed },
            { label: "Declined / No Show", value: declined },
          ].map((m, i) => (
            <div className="col" key={i}>
              <div className="card metrics-card h-100">
                <div className="card-body">
                  <h6 className="text-secondary mb-1">{m.label}</h6>
                  <div className="fs-2 fw-bold">{m.value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ✅ Charts */}
        <div className="row g-3 mb-4">
          <div className="col-md-6">
            <div className="card chart-card h-100">
              <div className="card-header">Restaurant Names</div>
              <div className="card-body">
                <Doughnut data={restaurantDonut} options={doughnutOptions} />
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card chart-card h-100">
              <div className="card-header">Status Breakdown (Completed Only)</div>
              <div className="card-body">
                <Doughnut data={statusDonut} options={doughnutOptions} />
              </div>
            </div>
          </div>
        </div>

        {/* ✅ Toolbar */}
        <div className="card mb-4 search-filter-card">
          <div className="card-body searchbar-row-inline">
            <span className="search-icon" aria-hidden="true">
              <i className="bi bi-search" />
            </span>

            <div className="search-wrap">
              <input
                type="text"
                className="form-control search-pill"
                placeholder="Search by reservation id, name, phone, restaurant..."
                aria-label="Search reservations"
              />
            </div>

            <div className="date-range">
              <input type="date" className="date-pill" aria-label="From date" />
              <span className="range-sep">to</span>
              <input type="date" className="date-pill" aria-label="To date" />
            </div>

            <div className="exports">
              <button type="button" className="btn btn-success" onClick={handleExportExcel}>
                Export Excel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleExportPDF}>
                Export PDF
              </button>
            </div>
          </div>
        </div>

        {/* ✅ Table (fixed spacing) */}
        <div className="card dining-table-card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Reservations List</h5>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0 align-middle table-pro">
                <colgroup>
                  <col style={{ width: "80px" }} />
                  <col style={{ width: "180px" }} />
                  <col style={{ width: "200px" }} />
                  <col style={{ width: "160px" }} />
                  <col style={{ width: "220px" }} />
                  <col style={{ width: "140px" }} />
                  <col style={{ width: "220px" }} />
                  <col style={{ width: "260px" }} />
                  <col style={{ width: "140px" }} />
                </colgroup>

                <thead>
                  <tr>
                    <th>SI.NO</th>
                    <th>Reservation ID</th>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Restaurant</th>
                    <th>No. of Guests</th>
                    <th>Date &amp; Time</th>
                    <th>Special Request</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td colSpan="9" className="text-center py-4 text-muted">
                      No reservations found yet.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <footer className="dashboard-footer">
        <small>
          © {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved
        </small>
      </footer>
    </div>
  );
}
